
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight, CheckCircle, Calendar, Users, Building, Target, TrendingUp, Shield, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { caseStudies } from "@/data/caseStudyData";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";
import { useEffect } from "react";

const RegionalRestaurantChainTransformation = () => {
  // Find the case study data - using the correct ID format that matches the data file
  const caseStudy = caseStudies.find(study => study.id === "RegionalRestaurantChainTransformation");
  const companyName = caseStudy?.redactedName || "Coastal Dining Collective";
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  if (!caseStudy) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="container mx-auto px-4 pt-24 pb-16">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-gray-900">Case Study Not Found</h1>
            <p className="mt-4 text-gray-600">The case study you're looking for could not be found.</p>
            <div className="mt-8">
              <Link to="/case-studies">
                <Button variant="outline" className="font-medium">
                  Browse All Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <BreadcrumbNav currentPage="Regional Restaurant Chain IT Transformation" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    Case Study: {caseStudy.industry}
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  How <RedactedCompanyName companyName={companyName} /> Modernized Their Multi-Location IT Infrastructure
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Learn how we helped a regional restaurant chain with 30+ locations transform their outdated IT systems into a secure, cloud-based infrastructure.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/get-started">
                    <Button 
                      size="lg"
                      className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md group"
                    >
                      Get Similar Results
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <a href="#challenge">
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-purple-300 text-purple-800 hover:bg-purple-50"
                    >
                      Read the Case Study
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Card className="bg-white border-0 shadow-xl rounded-xl overflow-hidden">
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">{caseStudy.industry}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Size</p>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">{caseStudy.companyType}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Project Duration</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">5 Months</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Focus Areas</p>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Cloud Migration & Security</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Key Results:</h3>
                      <div className="space-y-2">
                        {caseStudy.results.map((result, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle className="h-3 w-3 text-green-600" />
                            </div>
                            <p className="text-gray-700">{result.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="challenge" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-4">
                  The Challenge
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Outdated Infrastructure Across Multiple Locations
                </h2>
                <p className="text-lg text-gray-600">
                  <RedactedCompanyName companyName={companyName} /> was struggling with fragmented and outdated IT systems across their 30+ restaurant locations.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Legacy On-Premises Servers</h3>
                  <p className="text-gray-600 mb-4">
                    Each location had aging on-premises servers that were expensive to maintain and created reliability issues.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Frequent system outages disrupting operations</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Inconsistencies</h3>
                  <p className="text-gray-600 mb-4">
                    Inconsistent security policies across locations led to varying levels of protection for sensitive customer and business data.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Low Microsoft Secure Score of 45 points</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Limited Mobility</h3>
                  <p className="text-gray-600 mb-4">
                    Managers needed to access systems across multiple restaurant locations but had limited mobility options.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Managers tied to physical locations for system access</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Siloed Communication</h3>
                  <p className="text-gray-600 mb-4">
                    Fragmented communication systems created silos between corporate and individual restaurant teams.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Inefficient information sharing across locations</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Breaking Point
                    </h3>
                    <p className="text-gray-700">
                      "We were drowning in hardware costs and constantly fighting fires with our aging servers. Each location had its own way of doing things, which made managing security and standardizing operations nearly impossible."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— Operations Director at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Solution Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  The Solution
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Comprehensive Microsoft 365 Migration Strategy
                </h2>
                <p className="text-lg text-gray-600">
                  {caseStudy.solution}
                </p>
              </div>
              
              <div className="space-y-8 mb-12">
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Cloud Migration Planning</h3>
                      <p className="text-gray-600 mb-4">
                        We developed a phased migration plan that minimized operational disruption while transitioning each location from on-premises systems to Microsoft 365.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Designed location-specific migration schedules to prevent business interruption</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented hybrid configurations during transition phases</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Created data migration protocols to maintain business continuity</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Target className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Standardized Security Architecture</h3>
                      <p className="text-gray-600 mb-4">
                        We implemented consistent security policies, advanced threat protection, and compliance controls across all locations to ensure uniform protection.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Deployed Microsoft Defender for comprehensive protection across endpoints</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Established uniform identity protection with conditional access policies</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented centralized security monitoring with automated alerts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Unified Communication Platform</h3>
                      <p className="text-gray-600 mb-4">
                        We deployed Microsoft Teams with customized channels for corporate-to-store and store-to-store collaboration to break down communication silos.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Created standardized Teams structure aligned with organizational hierarchy</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Configured role-based access controls for appropriate information sharing</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented document management systems with consistent structure and permissions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {caseStudy.testimonial && (
                <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-white border border-purple-200 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        The Transformation
                      </h3>
                      <p className="text-gray-700">
                        {caseStudy.testimonial.quote}
                      </p>
                      <p className="text-sm font-medium text-gray-700 mt-2">— {caseStudy.testimonial.author}, {caseStudy.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  The Results
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Secure, Mobile, and Cost-Effective Operations
                </h2>
                <p className="text-lg text-gray-600">
                  Within five months, <RedactedCompanyName companyName={companyName} /> achieved significant improvements in security, mobility, and operational efficiency.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">222%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Security Score Improvement</h3>
                  <p className="text-gray-600">
                    Microsoft Secure Score increased from 45 to 145 points, dramatically improving data protection across all locations.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">30%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Cost Reduction</h3>
                  <p className="text-gray-600">
                    Achieved 30% reduction in annual security spending through tool consolidation and elimination of hardware costs.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">100%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Locations Migrated</h3>
                  <p className="text-gray-600">
                    Successfully migrated all 30+ locations to Microsoft 365 with zero business disruption during the transition.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">86%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Manager Satisfaction</h3>
                  <p className="text-gray-600">
                    Improved manager satisfaction with IT systems by 86% through mobile access and standardized tools.
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 p-8 rounded-xl border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Long-Term Impact
                    </h3>
                    <p className="text-gray-700">
                      "The migration was seamless, and our team now has secure access to everything they need from any location. We've finally eliminated our dependency on outdated servers while actually improving security."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— Operations Director at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Ready to Modernize Your Multi-Location Business?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Transform your IT infrastructure with a secure, cloud-based approach designed for multi-location operations.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/get-started">
                  <Button 
                    size="lg"
                    className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 transition-colors px-8 py-6 h-auto shadow-lg hover:shadow-xl group"
                  >
                    Schedule Your Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/services/microsoft-365-migration">
                  <Button
                    variant="outline" 
                    size="lg"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 h-auto"
                  >
                    Explore Cloud Migration Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related Case Studies Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Case Studies</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link to="/case-studies/optimized-m365-security-transformation" className="block">
                  <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 hover:shadow-md transition-shadow">
                    <div className="mb-2">
                      <span className="text-sm font-medium text-purple-700">Financial Services</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Financial Services Firm Optimizes Microsoft 365 Security
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Learn how a financial firm saved $320K annually while improving security posture.
                    </p>
                    <div className="flex items-center text-purple-700 font-medium">
                      Read Case Study
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </Link>
                
                <Link to="/case-studies/oregon-ski-resort-secures-seasonal" className="block">
                  <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 hover:shadow-md transition-shadow">
                    <div className="mb-2">
                      <span className="text-sm font-medium text-purple-700">Recreation & Tourism</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Oregon Ski Resort Secures Seasonal Operations
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      See how this resort managed seasonal staff security and payment data protection.
                    </p>
                    <div className="flex items-center text-purple-700 font-medium">
                      Read Case Study
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RegionalRestaurantChainTransformation;
